<div class="h-full min-h-0 flex flex-col w-full overflow-hidden gap-y-3">
  <!-- Year + exports -->
  <div class="shrink-0 flex flex-row flex-wrap items-center justify-between gap-3">
    <div class="flex flex-row items-center gap-x-2">
      <span class="text-xs font-semibold uppercase tracking-wide text-secondary">Tax year</span>
      @for (year of yearOptions(); track year) {
      <button
        type="button"
        class="rounded-md px-3 py-1.5 text-sm font-medium cursor-pointer"
        [class]="getYearClass(year)"
        (click)="selectYear(year)"
      >
        {{ year }}
      </button>
      }
    </div>

    <div class="flex flex-row items-center gap-x-2">
      <input
        [formField]="searchQueryForm.query"
        type="search"
        placeholder="Search name, email or ID"
        class="rounded-md border border-gray-300 dark:border-white/10 bg-transparent px-3 py-1.5 text-sm w-56"
      />
      <button
        type="button"
        class="rounded-md px-3 py-1.5 text-sm font-medium cursor-pointer bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200"
        (click)="downloadReadinessCsv()"
      >
        Export ledger CSV
      </button>
      <button
        type="button"
        class="rounded-md px-3 py-1.5 text-sm font-medium cursor-pointer bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200"
        (click)="downloadPostalCsv()"
      >
        Postal delivery CSV
      </button>
    </div>
  </div>

  <!-- Bucket tabs -->
  <div class="shrink-0 flex flex-row flex-wrap items-center gap-x-2">
    @for (tab of tabs; track tab.id) {
    <button
      type="button"
      class="rounded-md px-3 py-2 text-sm font-medium cursor-pointer"
      [class]="getTabClass(tab.id)"
      (click)="selectTab(tab.id)"
    >
      {{ tab.label }}
      <span class="ml-1 text-xs opacity-70">{{ statFor(tab.id) }}</span>
    </button>
    }
  </div>

  <p class="shrink-0 text-xs text-secondary">{{ activeHint() }}</p>

  <!--
    Airwallex is the system of record for filing; this console cannot file. Saying so
    where the operator works prevents the reasonable-but-wrong assumption that marking a
    row "filed" here submits anything to the IRS.
  -->
  <div
    class="shrink-0 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
  >
    Filing happens in the Airwallex web app (Transfers → Tax Filing). This console shows who
    is ready, exports our ledger so you can diff it against theirs, and records what was
    filed so the creator app can show a real filing date.
  </div>

  <!-- Reconciliation warnings -->
  @if (unlinkedCount() > 0 || mismatchCount() > 0) {
  <div
    class="shrink-0 rounded-md border border-red-300/60 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-900 dark:text-red-200 flex flex-col gap-y-1"
  >
    @if (unlinkedCount() > 0) {
    <span>
      <strong>{{ unlinkedCount() }}</strong> creator(s) have no confirmed Airwallex external
      identifier. Their transfers may not be associated with their W-9, so Airwallex would
      report nothing for them. Note Airwallex only applies a new identifier to future transfers.
    </span>
    } @if (mismatchCount() > 0) {
    <span>
      <strong>{{ mismatchCount() }}</strong> creator(s) have a ledger total that disagrees with
      what Airwallex reported. Resolve before the creator sees a breakdown that doesn't add up.
    </span>
    }
  </div>
  }

  <!-- Table -->
  <div class="data-table-wrapper flex-1 min-h-0 overflow-auto" (scroll)="onTableScroll($event)">
    @if (rows() === undefined) {
    <div class="flex items-center justify-center py-16 text-sm text-secondary">Loading…</div>
    } @else if (loadError()) {
    <div class="flex flex-col items-center justify-center py-16 gap-y-3">
      <span class="text-sm text-secondary">{{ loadError() }}</span>
      <button
        type="button"
        class="rounded-md px-3 py-1.5 text-sm font-medium cursor-pointer bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200"
        (click)="reload()"
      >
        Try again
      </button>
    </div>
    } @else if (rows()!.length === 0) {
    <div class="flex items-center justify-center py-16 text-sm text-secondary">
      No US payees with settled earnings in {{ selectedYear() }}.
    </div>
    } @else {
    <table class="data-table min-w-full">
      <thead class="sticky top-0 z-10">
        <tr>
          <th class="text-left">Creator</th>
          <th class="text-left">Status</th>
          <th class="text-left">W-9</th>
          <th class="text-left">Delivery</th>
          <th class="text-right">Ledger total</th>
          <th class="text-right">Reported</th>
          <th class="text-right">Threshold</th>
          <th class="text-left">Filed</th>
          <th class="text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        @for (row of rows()!; track row.blazeFirebaseUid) {
        <tr>
          <td>
            <div class="flex flex-col">
              <span class="font-medium">{{ row.creatorName }}</span>
              <span class="text-xs text-secondary">{{ row.email || row.blazeFirebaseUid }}</span>
              @if (!row.externalIdentifierOk) {
              <span class="text-xs text-red-600 dark:text-red-400">Airwallex linkage unconfirmed</span>
              } @if (row.nonUsdMinor > 0) {
              <span class="text-xs text-amber-700 dark:text-amber-300">
                + {{ toMajor(row.nonUsdMinor) | currency: 'USD' }} paid in another currency (excluded)
              </span>
              }
            </div>
          </td>
          <td>
            <span class="rounded-full px-2 py-0.5 text-xs font-semibold" [class]="bucketClass(row.bucket)">
              {{ bucketLabel(row.bucket) }}
            </span>
          </td>
          <td class="text-xs">
            {{ row.taxFormType || '—' }}
            <div class="text-secondary">{{ row.profileStatus }}</div>
          </td>
          <td class="text-xs">{{ row.deliveryPreference || '—' }}</td>
          <td class="text-right tabular-nums">{{ toMajor(row.totalMinor) | currency: 'USD' }}</td>
          <td class="text-right tabular-nums" [class.text-red-600]="hasMismatch(row)">
            {{ row.reportedAmountMinor === null ? '—' : (toMajor(row.reportedAmountMinor) | currency: 'USD') }}
          </td>
          <td class="text-right tabular-nums text-secondary">
            {{ toMajor(row.thresholdMinor) | currency: 'USD' }}
          </td>
          <td class="text-xs">
            {{ row.filedDate ? (row.filedDate | date: 'mediumDate') : '—' }}
          </td>
          <td class="text-right">
            <div class="flex flex-row items-center justify-end gap-x-2">
              @if (canMarkFiled(row)) {
              <button
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium cursor-pointer bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300"
                (click)="markFiled(row)"
              >
                Mark filed
              </button>
              }
              @if (row.profileStatus === 'submitted') {
              <button
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium cursor-pointer bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300"
                (click)="setProfileStatus(row, 'verified')"
              >
                TIN matched
              </button>
              <button
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium cursor-pointer bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300"
                (click)="setProfileStatus(row, 'invalid')"
              >
                TIN failed
              </button>
              }
              <button
                type="button"
                class="rounded-md px-2 py-1 text-xs font-medium cursor-pointer bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300"
                (click)="syncBeneficiary(row)"
              >
                Check linkage
              </button>
            </div>
          </td>
        </tr>
        }
      </tbody>
    </table>

    @if (loadingMore()) {
    <div class="flex items-center justify-center py-4 text-xs text-secondary">Loading more…</div>
    }
    }
  </div>
</div>
